import { Layout, Typography } from 'antd';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';

import {
    CertificationVerificationStatus,
    DhsBackgroundCheckStatus,
    LicenseStatus,
    ListProfileSpecialtyDto,
    Roles,
    SpecialtyDto,
} from '@clh/api-client';

import { useApiClient } from '../hooks/use-api-client';
import { Content, Header } from '../layout';
import Spinner from '../spinner/spinner';
import { DrilldownTable, FilterSelect } from '../table';

import { Tabs } from './tabs';

const Text = Typography.Text;

export default function Profiles() {
    const api = useApiClient();
    const [specialties, setSpecialties] = useState<SpecialtyDto[]>([]);

    useEffect(() => {
        if (!api) {
            return;
        }
        api.specialtyControllerGetSpecialties()
            .then((result) => setSpecialties(result.filter((s) => s.active)))
            // biome-ignore lint/suspicious/noConsole: surface load failure
            .catch((e) => console.error(e));
    }, [api]);

    if (!api) {
        return <Spinner />;
    }

    const specialtyNameByCode = new Map(
        specialties.map((s) => [s.code, s.name])
    );

    return (
        <Layout>
            <Header>
                <h2>Profiles</h2>
            </Header>
            <Content>
                <DrilldownTable
                    paginated
                    filters={{
                        search: 'search',
                        searchPlaceholder: 'Enter name or email',
                        filters: {
                            dhsBackgroundCheckStatus: {
                                label: 'DHS Background Check Status',
                                control: ({ onApply, onCancel }) => (
                                    <FilterSelect
                                        onApply={onApply}
                                        onCancel={onCancel}
                                        style={{ width: 180 }}
                                        placeholder="DHS Background Check Status"
                                        options={Object.entries(
                                            DhsBackgroundCheckStatus
                                        ).map((entry) => ({
                                            label: entry[0],
                                            value: entry[1],
                                        }))}
                                    />
                                ),
                            },
                            certificationVerificationStatus: {
                                label: 'AHA Certifications',
                                control: ({ onApply, onCancel }) => (
                                    <FilterSelect
                                        onApply={onApply}
                                        onCancel={onCancel}
                                        style={{ width: 180 }}
                                        placeholder="AHA Certifications"
                                        options={Object.entries(
                                            CertificationVerificationStatus
                                        ).map((entry) => ({
                                            label: entry[0],
                                            value: entry[1],
                                        }))}
                                    />
                                ),
                            },
                            hasExpiredCertification: {
                                label: 'Expired Certifications',
                                control: ({ onApply, onCancel }) => (
                                    <FilterSelect
                                        onApply={onApply}
                                        onCancel={onCancel}
                                        style={{ width: 180 }}
                                        placeholder="Has Expired Certifications"
                                        options={['true', 'false'].map(
                                            (entry) => ({
                                                label: entry,
                                                value: entry,
                                            })
                                        )}
                                    />
                                ),
                            },
                            hasExpiredLicense: {
                                label: 'Expired License',
                                control: ({ onApply, onCancel }) => (
                                    <FilterSelect
                                        onApply={onApply}
                                        onCancel={onCancel}
                                        style={{ width: 180 }}
                                        placeholder="Has Expired License"
                                        options={['true', 'false'].map(
                                            (entry) => ({
                                                label: entry,
                                                value: entry,
                                            })
                                        )}
                                    />
                                ),
                            },
                            licenseStatus: {
                                label: 'License Status',
                                control: ({ onApply, onCancel }) => (
                                    <FilterSelect
                                        onApply={onApply}
                                        onCancel={onCancel}
                                        style={{ width: 180 }}
                                        placeholder="License Status"
                                        options={Object.entries(
                                            LicenseStatus
                                        ).map((entry) => ({
                                            label: entry[0],
                                            value: entry[1],
                                        }))}
                                    />
                                ),
                            },
                            hasExhaustedAssessment: {
                                label: 'Exhausted Assessment',
                                control: ({ onApply, onCancel }) => (
                                    <FilterSelect
                                        onApply={onApply}
                                        onCancel={onCancel}
                                        style={{ width: 180 }}
                                        placeholder="Has Exhausted Assessment"
                                        options={['true', 'false'].map(
                                            (entry) => ({
                                                label: entry,
                                                value: entry,
                                            })
                                        )}
                                    />
                                ),
                            },
                            role: {
                                label: 'Account Type',
                                control: ({ onApply, onCancel }) => (
                                    <FilterSelect
                                        onApply={onApply}
                                        onCancel={onCancel}
                                        style={{ width: 180 }}
                                        placeholder="Account Type"
                                        options={Object.entries(Roles).map(
                                            (entry) => ({
                                                label: entry[0],
                                                value: entry[1],
                                            })
                                        )}
                                    />
                                ),
                            },
                            specialtyCodes: {
                                label: 'Specialty',
                                displayValue: (value) =>
                                    Array.isArray(value)
                                        ? (specialtyNameByCode.get(value[0]) ??
                                          value[0])
                                        : value,
                                control: ({ onApply, onCancel }) => (
                                    <FilterSelect
                                        onApply={onApply}
                                        onCancel={onCancel}
                                        style={{ width: 220 }}
                                        placeholder="Specialty"
                                        showSearch
                                        optionFilterProp="label"
                                        options={specialties.map((s) => ({
                                            label: s.name,
                                            value: s.code,
                                        }))}
                                        onChange={(value) =>
                                            onApply(value ? [value] : undefined)
                                        }
                                    />
                                ),
                            },
                            isReadyToWork: {
                                label: 'Ready to Work',
                                displayValue: (value) =>
                                    value === 'true' ? 'Yes' : 'No',
                                control: ({ onApply, onCancel }) => (
                                    <FilterSelect
                                        onApply={onApply}
                                        onCancel={onCancel}
                                        style={{ width: 180 }}
                                        placeholder="Ready to Work"
                                        options={[
                                            { label: 'Yes', value: 'true' },
                                            { label: 'No', value: 'false' },
                                        ]}
                                    />
                                ),
                            },
                        },
                    }}
                    getRecordById={(id) =>
                        api
                            .profileControllerGetProfile({ profileId: id })
                            .then((r) => ({
                                ...r,
                                isNurse: false,
                                isFacilityAdmin: false,
                            }))
                    }
                    getRecords={(params) => {
                        return api.profileControllerListProfiles(params);
                    }}
                    cacheKey={'profileControllerListProfiles'}
                    columns={[
                        {
                            dataIndex: 'id',
                            title: 'ID',
                            render: (value: string) => (
                                <Text code>{value.slice(0, 8)}</Text>
                            ),
                        },
                        {
                            dataIndex: 'firstName',
                            title: 'First Name',
                            sorter: true,
                        },
                        {
                            dataIndex: 'lastName',
                            title: 'Last Name',
                            sorter: true,
                        },
                        {
                            dataIndex: 'email',
                            title: 'Email',
                            sorter: true,
                        },
                        {
                            dataIndex: 'phoneNumber',
                            title: 'Phone',
                            render: (value: string | null) => {
                                if (!value) return null;
                                const l = value.length;
                                const prefix = value.slice(0, l - 10);
                                const area = value.slice(l - 10, l - 7);
                                const mid = value.slice(l - 7, l - 4);
                                const last = value.slice(l - 4);
                                return `${prefix} (${area}) ${mid}-${last}`.trim();
                            },
                        },
                        {
                            dataIndex: 'homeState',
                            title: 'Home State',
                            sorter: true,
                        },
                        {
                            dataIndex: 'createdAt',
                            title: 'Created At',
                            sorter: true,
                            render: (value: Date) => (
                                <>
                                    {DateTime.fromJSDate(value).toLocaleString(
                                        DateTime.DATETIME_SHORT
                                    )}
                                </>
                            ),
                        },
                        {
                            title: 'Account Type',
                            render: (profile) => profile.roles.join(', '),
                        },
                        {
                            title: 'Specialties',
                            render: (profile) =>
                                profile.specialties?.map(
                                    (s: ListProfileSpecialtyDto) => (
                                        <div key={s.specialtyCode}>
                                            {specialtyNameByCode.get(
                                                s.specialtyCode
                                            ) ?? s.specialtyCode}
                                        </div>
                                    )
                                ),
                        },
                    ]}
                >
                    <Tabs />
                </DrilldownTable>
            </Content>
        </Layout>
    );
}
